import type { FieldFilter, FilterPathSegment, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, PrimitiveFieldPath, PrimitivePathSegment, SegmentedFieldFilter, ToManyPathSegment, ToOnePathSegment } from '#layers/data-model/public';
import type {
	FilterSchemaCollection,
	FilterSchemaField,
	FilterSchemaPrimitiveField,
} from './filter-schema';
import { isFilterOperator, isFilterRelationalQuantifier } from '#layers/data-model/public';

export function isPrimitiveSegment(s: FilterPathSegment): s is PrimitivePathSegment {
	return s.type === 'primitive';
}

export function isToOneSegment(s: FilterPathSegment): s is ToOnePathSegment {
	return s.type === 'to-one';
}

export function isToManySegment(s: FilterPathSegment): s is ToManyPathSegment {
	return s.type === 'to-many';
}

export function isRelationalSegment(s: FilterPathSegment): s is ToOnePathSegment | ToManyPathSegment {
	return isToOneSegment(s) || isToManySegment(s);
}

function isPrimitiveFilterField(field: FilterSchemaField): field is FilterSchemaPrimitiveField {
	return 'type' in field && 'dbName' in field;
}

// Lookup interface used by filter parsing and traversal helpers.
export interface FilterSchemaLookup {
	findFieldById: (id: GlobalFieldId) => FilterSchemaField | undefined;
	findFieldByApiName: (collection: FilterSchemaCollection, apiName: string) => FilterSchemaField | undefined;
	findOppositeCollectionByRelationFieldId: (id: GlobalSingleRelationFieldId) => FilterSchemaCollection | undefined;
}

export function serializeSegmentedFilter(
	filter: SegmentedFieldFilter,
	schemaLookup: FilterSchemaLookup,
): FieldFilter {
	const { segments, operator, value } = filter;

	if (segments.length === 0) {
		throw new Error('SegmentedFieldFilter must have at least one segment');
	}

	const lastSegment = segments.at(-1)!;

	if (!isPrimitiveSegment(lastSegment)) {
		throw new Error('SegmentedFieldFilter must end with a primitive segment');
	}

	return segments.reduceRight((inner, segment) => {
		const field = schemaLookup.findFieldById(segment.fieldId);

		if (!field) {
			throw new Error(`Field not found: ${segment.fieldId}`);
		}

		const apiName = field.apiName;

		if (isToManySegment(segment)) {
			return { [apiName]: { [segment.quantifier]: inner } };
		}

		return { [apiName]: inner };
	}, operator ? { [operator]: value } : {} as FieldFilter);
}

export function parseSegmentedFilter(
	filter: FieldFilter,
	collection: FilterSchemaCollection,
	schemaLookup: FilterSchemaLookup,
): SegmentedFieldFilter {
	const segments: FilterPathSegment[] = [];
	let currentCollection = collection;
	let currentObj = filter;

	while (true) {
		const keys = Object.keys(currentObj);

		if (keys.length === 0) {
			// Empty object = incomplete filter (operator/value lost to JSON serialization)
			return { segments, operator: undefined, value: undefined };
		}

		const key = keys[0]!;

		if (isFilterOperator(key)) {
			return { segments, operator: key, value: currentObj[key] };
		}

		const field = schemaLookup.findFieldByApiName(currentCollection, key);

		if (!field) {
			throw new Error(`Unknown field '${key}' in collection '${currentCollection.apiName}'`);
		}

		if (isPrimitiveFilterField(field)) {
			segments.push({ type: 'primitive', fieldId: field.id as GlobalPrimitiveFieldId });
			currentObj = currentObj[key] as FieldFilter;
		}
		else if (field.isList) {
			const inner = currentObj[key] as Record<string, unknown>;
			const firstInnerKey = Object.keys(inner)[0];
			const hasExplicitQuantifier = firstInnerKey && isFilterRelationalQuantifier(firstInnerKey);
			const quantifier = hasExplicitQuantifier ? firstInnerKey : '_some';

			segments.push({ type: 'to-many', fieldId: field.id as GlobalSingleRelationFieldId, quantifier });
			currentObj = (hasExplicitQuantifier ? inner[quantifier] : inner) as FieldFilter;
		}
		else {
			segments.push({ type: 'to-one', fieldId: field.id as GlobalSingleRelationFieldId });
			currentObj = currentObj[key] as FieldFilter;
		}

		if (!isPrimitiveFilterField(field)) {
			const oppositeCollection = schemaLookup.findOppositeCollectionByRelationFieldId(field.id as GlobalSingleRelationFieldId);

			if (!oppositeCollection) {
				throw new Error(`[parseSegmentedFilter]: could not determine opposite collection of ${field.apiName}(${field.id})`);
			}

			currentCollection = oppositeCollection;
		}
	}
}

export function buildSegmentsFromFieldIds(
	fieldPath: PrimitiveFieldPath,
	collection: FilterSchemaCollection,
	schemaLookup: Pick<FilterSchemaLookup, 'findOppositeCollectionByRelationFieldId'>,
): FilterPathSegment[] {
	const segments: FilterPathSegment[] = [];
	let currentCollection = collection;

	for (const fieldId of fieldPath) {
		const primitive = currentCollection.primitiveFields.find((f) => f.id === fieldId);

		if (primitive) {
			segments.push({ type: 'primitive', fieldId: fieldId as GlobalPrimitiveFieldId });
			break; // Primitive is always last
		}

		const relation = currentCollection.relationFields?.find((f) => f.id === fieldId);

		if (relation) {
			if (relation.isList) {
				segments.push({ type: 'to-many', fieldId: fieldId as GlobalSingleRelationFieldId, quantifier: '_some' });
			}
			else {
				segments.push({ type: 'to-one', fieldId: fieldId as GlobalSingleRelationFieldId });
			}

			const oppositeCollection = schemaLookup.findOppositeCollectionByRelationFieldId(fieldId as GlobalSingleRelationFieldId);

			if (!oppositeCollection) {
				throw new Error(`[buildSegmentsFromFieldIds]: Could not determine opposite collection for relation field '${relation.apiName}' (${relation.id})`);
			}

			currentCollection = oppositeCollection;
		}
		else {
			throw new Error(`[buildSegmentsFromFieldIds]: Unknown field ID '${fieldId}' in collection '${currentCollection.apiName}'`);
		}
	}

	return segments;
}

export type { FilterPathSegment, PrimitivePathSegment, SegmentedFieldFilter, ToManyPathSegment, ToOnePathSegment } from '#layers/data-model/public';
